/**
 * Memorable machine names (ADR 0105, the owner's ask of 23 Oct 2026): a cloud VM Orgus sets up
 * gets a name people remember and say out loud, an exciting word and a famous name
 * ("cosmic-lovelace", "blazing-tesla"), instead of the provider's host name
 * ("ip-172-31-30-101"). Host-name safe (CLOUD_MACHINE_NAME_RE), lower case, at most 63
 * characters, never one the fleet already has. Browser-safe, no dependencies.
 */

/** Exciting words: short, positive, easy to say. */
export const NAME_WORDS = [
  'blazing', 'bold', 'brave', 'bright', 'cosmic', 'daring', 'dazzling', 'electric', 'epic', 'fearless',
  'fiery', 'galactic', 'golden', 'hyper', 'legendary', 'lucky', 'mighty', 'quantum', 'rapid', 'rocket',
  'solar', 'stellar', 'swift', 'thunder', 'turbo', 'vivid', 'wild', 'zippy',
] as const;

/** Famous names: scientists, pioneers, explorers and stars people know at once. */
export const NAME_LEGENDS = [
  'lovelace', 'turing', 'hopper', 'curie', 'tesla', 'einstein', 'galileo', 'kepler', 'newton', 'darwin',
  'hawking', 'sagan', 'feynman', 'noether', 'ramanujan', 'aryabhata', 'franklin', 'edison', 'faraday', 'pascal',
  'magellan', 'armstrong', 'gagarin', 'earhart', 'hubble', 'johnson', 'ride', 'tereshkova',
  'orion', 'apollo', 'artemis', 'phoenix', 'atlas', 'vega', 'nova', 'titan', 'falcon', 'comet',
] as const;

/**
 * A memorable name not in `taken` (compared ignoring case). `random` returns [0, 1); tests pass
 * a fixed sequence. After the word and legend pairs run out (over a thousand), a number follows.
 */
export function memorableMachineName(taken: Iterable<string> = [], random: () => number = Math.random): string {
  const used = new Set([...taken].map((n) => n.toLowerCase()));
  const pick = <T>(xs: readonly T[]) => xs[Math.min(xs.length - 1, Math.floor(random() * xs.length))]!;
  for (let i = 0; i < 64; i++) {
    const name = `${pick(NAME_WORDS)}-${pick(NAME_LEGENDS)}`;
    if (!used.has(name)) return name;
  }
  // Very full fleet: the same shape with a number, still unique.
  const stem = `${pick(NAME_WORDS)}-${pick(NAME_LEGENDS)}`;
  for (let n = 2; ; n++) {
    const name = `${stem}-${n}`;
    if (!used.has(name)) return name;
  }
}
